import { useFormContext } from 'react-hook-form';
import { useEffect } from 'react';
import {
  diaChiSearchColumns,
  khachHangSearchColumns,
  phuongThucGiaoHangSearchColumns,
  phuongTienVanChuyenSearchColumns,
  QUERY_KEYS
} from '@/constants';
import { DiaChi, KhachHang, PhuongThucGiaoHang, PhuongTienVanChuyen, TinhChatThue } from '@/types/schemas';
import { FormFieldV2, SearchFieldV2 as SearchField } from '@/components/custom/erp';
import { usePhieuNhapHangBanTraLaiSearchContext } from '../../context';
import { FormFieldActions, FormFieldState } from '../../hooks';
import { Label } from '@/components/ui/label';
import { FormMode } from '@/types/form';
import { useCRUD } from '@/hooks';

interface OtherTabProps {
  formMode: FormMode;
  formState: {
    state: FormFieldState;
    actions: FormFieldActions;
  };
}

const rowClassName = 'flex items-center gap-2 xl:gap-3';
const labelClassName = 'w-32 shrink-0 text-[13px] xl:w-40';

export default function OtherTab({ formMode, formState: { state, actions } }: OtherTabProps) {
  const { data } = useCRUD<TinhChatThue, BanHangPayload>({ endpoint: QUERY_KEYS.TINH_CHAT_THUE });
  const tinhChatThueOpts = data.map((item: BanHangRecord) => ({ value: item.uuid, label: item.ten_tc_thue }));
  const { setValue, watch } = useFormContext();
  const maTcThue = watch('ma_tc_thue');
  const maGD = watch('ma_gd');
  const { transactionOptions } = usePhieuNhapHangBanTraLaiSearchContext();

  useEffect(() => {
    if (maTcThue || tinhChatThueOpts.length === 0) return;

    setValue('ma_tc_thue', tinhChatThueOpts[0]?.value, {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: true
    });
  }, [maTcThue, setValue, tinhChatThueOpts]);

  useEffect(() => {
    if (maGD || transactionOptions.length === 0) return;

    setValue('ma_gd', transactionOptions[0]?.value, {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: true
    });
  }, [maGD, setValue, transactionOptions]);

  return (
    <div className='w-full overflow-x-auto bg-white'>
      <div className='w-full min-w-[1180px] space-y-2 p-3 xl:min-w-0 xl:p-4'>
        {/* Giao dịch */}
        <div className={rowClassName}>
          <Label className={labelClassName}>Giao dịch</Label>
          <FormFieldV2
            name='ma_gd'
            className='w-48'
            type='select'
            disabled={formMode === 'view'}
            options={transactionOptions}
          />
        </div>

        {/* Mã địa chỉ */}
        <div className={rowClassName}>
          <Label className={labelClassName}>Mã địa chỉ</Label>
          <SearchField<DiaChi>
            type='text'
            value={state.diaChi?.ma_dc || ''}
            searchEndpoint={`/${QUERY_KEYS.DIA_CHI}`}
            searchColumns={diaChiSearchColumns}
            columnDisplay='ma_dc'
            displayRelatedField='ten_dc'
            dialogTitle='Danh mục địa chỉ'
            onRowSelection={actions.setDiaChi}
            disabled={formMode === 'view'}
            className='w-100'
          />
        </div>

        {/* Kê thuế đầu vào */}
        <div className={rowClassName}>
          <div className='w-32 shrink-0 xl:w-40' />
          <FormFieldV2
            label='Kê thuế đầu vào'
            name='invat_yn'
            type='checkbox'
            disabled={formMode === 'view'}
            className='w-full'
            labelClassName='text-[13px] font-medium'
          />
        </div>

        {/* Phương tiện v/c & Ph/th giao hàng */}
        <div className={rowClassName}>
          <div className='flex min-w-0 flex-1 items-center gap-3'>
            <Label className={labelClassName}>Phương tiện v/c</Label>
            <SearchField<PhuongTienVanChuyen>
              type='text'
              disabled={formMode === 'view'}
              searchEndpoint={`/${QUERY_KEYS.PHUONG_TIEN_VAN_CHUYEN}`}
              searchColumns={phuongTienVanChuyenSearchColumns}
              columnDisplay='ma_ptvc'
              displayRelatedField='ten_ptvc'
              value={state.phuongTienVanChuyen?.ma_ptvc || ''}
              relatedFieldValue={state.phuongTienVanChuyen?.ten_ptvc || ''}
              onRowSelection={actions.setPhuongTienVanChuyen}
              dialogTitle='Danh mục phương tiện vận chuyển'
              className='w-100'
            />
          </div>

          <div className='flex min-w-0 flex-1 items-center gap-3'>
            <Label className={labelClassName}>Ph/th giao hàng</Label>
            <SearchField<PhuongThucGiaoHang>
              type='text'
              searchEndpoint={`/${QUERY_KEYS.PHUONG_THUC_GIAO_HANG}`}
              searchColumns={phuongThucGiaoHangSearchColumns}
              columnDisplay='ma_ptgh'
              displayRelatedField='ten_ptgh'
              value={state.phuongThucGiaoHang?.ma_ptgh || ''}
              relatedFieldValue={state.phuongThucGiaoHang?.ten_ptgh || ''}
              onRowSelection={actions.setPhuongThucGiaoHang}
              dialogTitle='Danh mục phương thức giao hàng'
              disabled={formMode === 'view'}
              className='w-100'
            />
          </div>
        </div>

        {/* Mẫu báo cáo & Mã tính chất */}
        <div className={rowClassName}>
          <div className='flex min-w-0 flex-1 items-center gap-3'>
            <Label className={labelClassName}>Mẫu báo cáo</Label>
            <FormFieldV2
              name='ma_mau_bc'
              type='select'
              disabled={formMode === 'view'}
              className='w-[200px]'
              options={[
                { value: '3', label: '3. Hóa đơn giá trị gia tăng' },
                { value: '4', label: '4. Hàng hóa, dịch vụ mua vào không có hóa đơn' },
                { value: '5', label: '5. Hóa đơn bán hàng thông thường' }
              ]}
            />
          </div>

          <div className='flex min-w-0 flex-1 items-center gap-3'>
            <Label className={labelClassName}>Mã tính chất</Label>
            <FormFieldV2
              name='ma_tc_thue'
              type='select'
              disabled={formMode === 'view'}
              className='w-[200px]'
              options={tinhChatThueOpts}
            />
          </div>
        </div>

        {/* Cục thuế */}
        <div className={rowClassName}>
          <Label className={labelClassName}>Cục thuế</Label>
          <SearchField<KhachHang>
            type='text'
            searchEndpoint={`/${QUERY_KEYS.KHACH_HANG}`}
            searchColumns={khachHangSearchColumns}
            columnDisplay='customer_code'
            displayRelatedField='customer_name'
            value={state.cucThue?.customer_code || ''}
            relatedFieldValue={state.cucThue?.customer_name || ''}
            onRowSelection={actions.setCucThue}
            dialogTitle='Danh mục đối tượng'
            disabled={formMode === 'view'}
            className='w-100'
          />
        </div>

        {/* Lý do thay thế/đ.ch */}
        <div className={rowClassName}>
          <Label className={labelClassName}>Lý do thay thế/đ.ch</Label>
          <div className='min-w-0 flex-1'>
            <FormFieldV2
              name='ly_do'
              type='text'
              disabled={formMode === 'view'}
              placeholder='Nhập lý do thay thế hoặc điều chỉnh'
            />
          </div>
        </div>

        {/* Ghi chú */}
        <div className={rowClassName}>
          <Label className={labelClassName}>Ghi chú</Label>
          <div className='min-w-0 flex-1'>
            <FormFieldV2 name='ghi_chu' type='text' disabled={formMode === 'view'} placeholder='Nhập ghi chú' />
          </div>
        </div>
      </div>
    </div>
  );
}
